export type MentionQuery = { query: string; start: number };

export type MentionPart = { isMention: boolean; start: number; text: string };

export type MentionEdit = { caret: number; text: string };

const SPACE = /\s/;

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const mentionPattern = (names: readonly string[]) => {
  const usable = [...new Set(names)].filter(name => name !== '').sort((a, b) => b.length - a.length);
  return usable.length === 0 ? null : new RegExp(`@(?:${usable.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}])`, 'gu');
};

export const mentionQueryAt = (text: string, caret: number): MentionQuery | null => {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start === -1) return null;
  if (start > 0 && !SPACE.test(before[start - 1] ?? '')) return null;
  const query = before.slice(start + 1);
  return SPACE.test(query) ? null : { query, start };
};

export const matchPeople = <Person extends { name: string }>(people: readonly Person[], query: string): Person[] => {
  const needle = query.toLocaleLowerCase();
  return people.filter(person => {
    const name = person.name.toLocaleLowerCase();
    return name.startsWith(needle) || name.split(SPACE).some(word => word.startsWith(needle));
  });
};

export const insertMention = (text: string, caret: number, start: number, name: string): MentionEdit => {
  const lead = start > 0 && !SPACE.test(text[start - 1] ?? '') ? ' ' : '';
  const inserted = `${lead}@${name} `;
  return { caret: start + inserted.length, text: text.slice(0, start) + inserted + text.slice(caret) };
};

export const mentionedIn = <Person extends { id: string; name: string }>(body: string, people: readonly Person[]): string[] => people.filter(person => mentionPattern([person.name])?.test(body) === true).map(person => person.id);

export const splitMentions = (body: string, names: readonly string[]): MentionPart[] => {
  const pattern = mentionPattern(names);
  if (pattern === null) return [{ isMention: false, start: 0, text: body }];
  const parts: MentionPart[] = [];
  let last = 0;
  for (const match of body.matchAll(pattern)) {
    if (match.index > last) parts.push({ isMention: false, start: last, text: body.slice(last, match.index) });
    parts.push({ isMention: true, start: match.index, text: match[0] });
    last = match.index + match[0].length;
  }
  if (last < body.length) parts.push({ isMention: false, start: last, text: body.slice(last) });
  return parts;
};
